import React, { useMemo } from 'react'
import { TextField, Chip } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { V4_T } from './propertyDetailHelpers'

// Coerce any id-ish value to a positive integer, or null.
const toId = (value) => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

// Build a fast lookup of propertyId -> option object.
const indexOptions = (options = []) => {
  const map = new Map()
  for (const opt of Array.isArray(options) ? options : []) {
    const id = toId(opt?.propertyId)
    if (id != null && !map.has(id)) map.set(id, opt)
  }
  return map
}

const optionLabel = (opt) => {
  if (!opt) return ''
  const name = opt.label || opt.companyName || `Property #${opt.propertyId}`
  return `${name} #${opt.propertyId}`
}

/**
 * Searchable multi-select for billing group members. Stores numeric Aspire
 * property ids under the hood but lets users search by name / company /
 * address. The current property is auto-pinned (non-removable, always
 * included) and is NOT part of `value` — the parent owns it via
 * `currentPropertyId`.
 *
 * When `propertyOptions` is empty it degrades to a free-solo input that
 * accepts raw numeric ids, preserving the legacy capability.
 */
function BillingGroupMemberPicker ({
  value = [],
  onChange,
  currentPropertyId,
  propertyOptions = [],
  error,
  helperText
}) {
  const currentId = toId(currentPropertyId)
  const byId = useMemo(() => indexOptions(propertyOptions), [propertyOptions])
  const hasOptions = byId.size > 0

  // Options exclude the current property (it's the pinned chip).
  const options = useMemo(
    () => Array.from(byId.values()).filter(opt => toId(opt.propertyId) !== currentId),
    [byId, currentId]
  )

  // Map the numeric value array into option objects for the Autocomplete.
  // Ids without a matching option still render as a bare-id chip so nothing
  // silently disappears.
  const selected = useMemo(() => {
    const seen = new Set()
    const out = []
    for (const raw of Array.isArray(value) ? value : []) {
      const id = toId(raw)
      if (id == null || id === currentId || seen.has(id)) continue
      seen.add(id)
      out.push(byId.get(id) || { propertyId: id, label: `Property #${id}` })
    }
    return out
  }, [value, byId, currentId])

  const emit = (nextOptions) => {
    if (!onChange) return
    const ids = []
    const seen = new Set()
    for (const item of nextOptions) {
      // freeSolo yields raw strings; option objects carry propertyId.
      const id = toId(typeof item === 'string' ? item : item?.propertyId)
      if (id == null || id === currentId || seen.has(id)) continue
      seen.add(id)
      ids.push(id)
    }
    onChange(ids)
  }

  return (
    <div>
      {currentId != null && (
        <Chip
          size="small"
          label={optionLabel(byId.get(currentId)) || `This property #${currentId}`}
          style={{
            marginBottom: 8,
            background: V4_T.green,
            color: '#fff',
            fontWeight: 600
          }}
        />
      )}
      <Autocomplete
        multiple
        size="small"
        freeSolo={!hasOptions}
        options={options}
        value={selected}
        filterSelectedOptions
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : optionLabel(opt))}
        getOptionSelected={(opt, val) => toId(opt?.propertyId) === toId(val?.propertyId)}
        onChange={(_e, next) => emit(next)}
        renderOption={(opt) => {
          if (typeof opt === 'string') return opt
          const name = opt.label || opt.companyName || `Property #${opt.propertyId}`
          return (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span style={{ fontWeight: 600, color: V4_T.ink }}>{name} <span style={{ color: V4_T.ink2, fontWeight: 400 }}>#{opt.propertyId}</span></span>
              {opt.address ? <span style={{ fontSize: 11, color: V4_T.ink2 }}>{opt.address}</span> : null}
            </div>
          )
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((opt, index) => (
            <Chip
              size="small"
              label={optionLabel(opt)}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={hasOptions ? 'Search properties by name, company, or address' : 'Type Aspire property IDs'}
            error={!!error}
            helperText={helperText || (hasOptions ? 'This property is always included.' : 'Property list unavailable — enter numeric IDs.')}
          />
        )}
      />
    </div>
  )
}

export default BillingGroupMemberPicker
